import { AuthenticationService, createHttpRequestParameters, HttpMethod, HttpService, HttpStatus } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import { FileHandlerConfiguration } from './file-service-types.js';
import { applyAcceptHeader, applyAuthorizationHeader, download, getFileInterface, isAllowedFile, isAllowedFileSize } from './file-service-utils.js';
class DefaultFileHandler {
    constructor() {
        this.progress = (event, events, file, request) => {
            if (!event.lengthComputable) {
                return;
            }
            const percentage = (event.loaded / event.total) * 100;
            const fileInterface = getFileInterface({ fileName: file.name, percentage, request, state: 'pending' });
            const { onProgress } = events;
            onProgress(fileInterface);
        };
        this.abort = (events, file, request) => {
            const fileInterface = getFileInterface({ fileName: file.name, percentage: 0, request, state: 'aborted' });
            const { onProgress } = events;
            onProgress(fileInterface);
        };
        this.error = (reject, file, request) => {
            const fileInterface = getFileInterface({ fileName: file.name, percentage: 0, request, state: 'error' });
            reject(fileInterface);
        };
        this.loadEnd = (resolve, reject, file, request) => {
            const { uploadResponseState, errorResponseState } = this.fileHandlerConfiguration;
            request.onreadystatechange = function () {
                if (request.readyState !== XMLHttpRequest.DONE || (request.status !== uploadResponseState && request.status !== errorResponseState)) {
                    return;
                }
                try {
                    const path = JSON.parse(request.response).path;
                    resolve(getFileInterface({ fileName: file.name, path, percentage: 100, request, state: 'done' }));
                }
                catch (e) {
                    reject(getFileInterface({ fileName: file.name, percentage: 0, request, state: 'error' }));
                }
            };
        };
        this.load = (events, file, request) => {
            const fileInterface = getFileInterface({ fileName: file.name, percentage: 100, request, state: 'load' });
            const { onProgress } = events;
            onProgress(fileInterface);
        };
        this.fileHandlerConfiguration = new FileHandlerConfiguration();
    }
    download(path, name, parameters = {}, method = HttpMethod.GET, options = {}) {
        const requestParameters = createHttpRequestParameters(method, path, parameters, options);
        return HttpService.fetch(requestParameters)
            .then((response) => {
            if (!response.body) {
                return new Blob();
            }
            return download(response.body, name);
        });
    }
    getPreviewURL(path) {
        const { previewUrl } = this.fileHandlerConfiguration;
        return fetch(previewUrl + path)
            .then(resp => resp.json())
            .then(data => {
            return data.url;
        });
    }
    preview(path) {
        const { previewUrl } = this.fileHandlerConfiguration;
        window.open(previewUrl + path);
    }
    upload(file, events, maxFileSize) {
        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();
            if (!isAllowedFile(file) || !isAllowedFileSize(file, maxFileSize)) {
                const fileInterface = getFileInterface({ fileName: file.name, percentage: 0, request, state: 'invalid' });
                reject(fileInterface);
                return;
            }
            request.upload.addEventListener('abort', () => this.abort(events, file, request));
            request.upload.addEventListener('error', () => this.error(reject, file, request));
            request.upload.addEventListener('load', () => this.load(events, file, request));
            request.upload.addEventListener('loadend', () => this.loadEnd(resolve, reject, file, request));
            request.upload.addEventListener('progress', (event) => this.progress(event, events, file, request));
            const formData = new FormData();
            formData.append('file', file, file.name);
            const { uploadUrl } = this.fileHandlerConfiguration;
            request.open('POST', uploadUrl, true);
            applyAcceptHeader(request);
            applyAuthorizationHeader(request);
            request.send(formData);
            request.onreadystatechange = function () {
                if (request.status === HttpStatus.UNAUTHORIZED) {
                    void AuthenticationService.requestRefreshToken().then(() => {
                        applyAuthorizationHeader(request);
                    });
                }
            };
        });
    }
    remove(path, name, onExecuteRemove) {
        const { removeResponseState, removeUrl } = this.fileHandlerConfiguration;
        const request = new XMLHttpRequest();
        request.open('DELETE', removeUrl, true);
        applyAcceptHeader(request);
        applyAuthorizationHeader(request);
        const formData = new FormData();
        formData.set('path', path);
        request.send(formData);
        request.onreadystatechange = function () {
            if (request.readyState != XMLHttpRequest.DONE) {
                return;
            }
            if (request.status === HttpStatus.UNAUTHORIZED) {
                void AuthenticationService.requestRefreshToken().then(() => {
                    applyAuthorizationHeader(request);
                });
            }
            if (isFunction(onExecuteRemove)) {
                onExecuteRemove(name, path, request.status, (request.status === removeResponseState));
            }
        };
    }
    setConfiguration(fileHandlerConfiguration) {
        this.fileHandlerConfiguration = fileHandlerConfiguration;
    }
}
export { DefaultFileHandler, };
//# sourceMappingURL=default-file-handler.js.map